import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../ProtectedRoute/ProtectedRoute';
import { useCliente } from '../../hooks/useCliente';
import { ClienteAuthModal } from '../Cliente/ClienteAuthModal';
import "./Header.css";

interface HeaderProps {
  onNavigate?: (page: string) => void;
  currentPage?: string;
}

const Header: React.FC<HeaderProps> = ({ onNavigate, currentPage }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showClienteAuthModal, setShowClienteAuthModal] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { isAdmin, isLocador } = useRole();
  const { cliente, isClienteCadastrado, logout: logoutCliente } = useCliente();

  // Memoizar as verificações de role para evitar re-renders desnecessários
  const userIsAdmin = useMemo(() => isAdmin(), [isAdmin]);
  const userIsLocador = useMemo(() => isLocador(), [isLocador]);

  // Forçar re-render quando cliente mudar
  useEffect(() => {
    // Este effect força o re-render quando o cliente muda
  }, [cliente]);

  // Escutar evento de atualização do cliente para forçar re-render
  useEffect(() => {
    const handleClienteUpdated = () => {
      // Forçar re-render do header quando o cliente for atualizado
      // O hook useCliente já atualiza o estado, mas garantimos que o header reaja
      // Forçando um re-render através de um estado local
      const clienteId = localStorage.getItem('clienteId');
      const clienteDataStr = localStorage.getItem('clienteData');
      if (clienteId && clienteDataStr) {
        try {
          // O estado do hook já deve estar atualizado, mas forçamos um re-render
          // através de um pequeno delay para garantir que o estado foi atualizado
          setTimeout(() => {
            // O componente já deve ter re-renderizado, mas garantimos
            window.dispatchEvent(new Event('resize'));
          }, 100);
        } catch (err) {
          console.error('Erro ao atualizar cliente no header:', err);
        }
      }
    };

    window.addEventListener('clienteUpdated', handleClienteUpdated);
    return () => {
      window.removeEventListener('clienteUpdated', handleClienteUpdated);
    };
  }, [cliente]);

  // Fechar menu do usuário ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isUserMenuOpen && !target.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isUserMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Effect para redirecionar usuários após login baseado no role
  useEffect(() => {
    if (isAuthenticated && user?.role && onNavigate) {
      // Pequeno delay para garantir que o estado foi atualizado
      setTimeout(() => {
        if (user.role === 'admin') {
          onNavigate('admin');
        } else if (user.role === 'locador') {
          onNavigate('locador');
        }
        // Locatários permanecem na página atual (home)
      }, 100);
    }
  }, [isAuthenticated, user?.role, onNavigate]);

  const handleLogout = async () => {
    logout();
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
    // Aguardar um momento antes de redirecionar para garantir que o estado foi limpo
    await new Promise(resolve => setTimeout(resolve, 100));
    // Sempre redireciona para home após logout
    navigate('/');
  };

  const handleClienteLogout = () => {
    logoutCliente();
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
    // Navegar para home após logout
    navigate('/');
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleNavigation = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <header 
        className="header header-home"
        style={{ 
          backgroundColor: '#ffffff',
          background: '#ffffff',
        }}
      >
        <div className="container" style={{ backgroundColor: 'transparent', background: 'transparent' }}>
          <div className="header-content" style={{ backgroundColor: 'transparent', background: 'transparent' }}>
            <div className="logo">
              <button 
                onClick={() => handleNavigation('home')}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}
                aria-label="Ir para página inicial"
              >
                <img 
                  src="/logo_sand_02.png" 
                  alt="SAND Imóveis" 
                  className="logo-image"
                />
              </button>
            </div>
            
            <nav className={`nav ${isMenuOpen ? "nav-open" : ""}`}>
              <ul className="nav-list">
                {isAuthenticated && user ? (
                  // Menu para usuários logados (admin/locador)
                  <>
                    <li className="user-menu-container">
                      <div className="user-info-clickable" onClick={toggleUserMenu}>
                        <span className="user-name">Olá, {user.nome}</span>
                        <span className="user-role">({user.role})</span>
                        <svg 
                          className={`user-menu-arrow ${isUserMenuOpen ? 'open' : ''}`}
                          width="12" 
                          height="12" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M6 9l6 6 6-6"></path>
                        </svg>
                      </div>
                      {isUserMenuOpen && (
                        <div className="user-menu-dropdown">
                          <button 
                            onClick={handleLogout} 
                            className="user-menu-item"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                              <polyline points="16 17 21 12 16 7"></polyline>
                              <line x1="21" y1="12" x2="9" y2="12"></line>
                            </svg>
                            Sair
                          </button>
                        </div>
                      )}
                    </li>
                    
                    {userIsLocador && (
                      <li><button 
                        onClick={() => handleNavigation('locador')} 
                        className={`nav-link ${currentPage === 'locador' ? 'active' : ''}`}
                      >
                        Meus Imóveis
                      </button></li>
                    )}
                    
                    {userIsAdmin && (
                      <li><button 
                        onClick={() => handleNavigation('admin')} 
                        className={`nav-link ${currentPage === 'admin' ? 'active' : ''}`}
                      >
                        Área Admin
                      </button></li>
                    )}
                    
                    <li><button 
                      onClick={() => handleNavigation('favoritos')} 
                      className={`nav-link ${currentPage === 'favoritos' ? 'active' : ''}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: 'middle' }}>
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                      </svg>
                      Favoritos
                    </button></li>
                  </>
                ) : (
                  // Menu para usuários não logados (visitantes)
                  <>
                    {cliente && isClienteCadastrado() ? (
                      // Cliente visitante logado
                      <>
                        <li className="user-menu-container">
                          <div className="user-info-clickable" onClick={toggleUserMenu}>
                            <span className="user-name">Olá, {cliente.nome}</span>
                            <svg 
                              className={`user-menu-arrow ${isUserMenuOpen ? 'open' : ''}`}
                              width="12" 
                              height="12" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                              <path d="M6 9l6 6 6-6"></path>
                            </svg>
                          </div>
                          {isUserMenuOpen && (
                            <div className="user-menu-dropdown">
                              <button 
                                onClick={handleClienteLogout} 
                                className="user-menu-item"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                  <polyline points="16 17 21 12 16 7"></polyline>
                                  <line x1="21" y1="12" x2="9" y2="12"></line>
                                </svg>
                                Sair
                              </button>
                            </div>
                          )}
                        </li>
                        <li><button onClick={() => handleNavigation('home')} className="nav-link">Home</button></li>
                        <li><button onClick={() => handleNavigation('about')} className={`nav-link ${currentPage === 'about' ? 'active' : ''}`}>Sobre nós</button></li>
                        <li><button onClick={() => handleNavigation('search')} className="nav-link">Buscar imóvel</button></li>
                        <li><button onClick={() => handleNavigation('favoritos')} className={`nav-link ${currentPage === 'favoritos' ? 'active' : ''}`}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: 'middle' }}>
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                          </svg>
                          Favoritos
                        </button></li>
                        <li><button onClick={() => handleNavigation('contato')} className={`nav-link ${currentPage === 'contato' ? 'active' : ''}`}>Contato</button></li>
                      </>
                    ) : (
                      // Visitante não logado
                      <>
                        <li>
                          <button 
                            onClick={() => setShowClienteAuthModal(true)}
                            className="nav-link"
                          >
                            <svg 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                              style={{ marginRight: '4px', verticalAlign: 'middle' }}
                            >
                              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                              <polyline points="10 17 15 12 10 7"></polyline>
                              <line x1="15" y1="12" x2="3" y2="12"></line>
                            </svg>
                            Login
                          </button>
                        </li>
                        <li><button onClick={() => handleNavigation('home')} className="nav-link">Home</button></li>
                        <li><button onClick={() => handleNavigation('about')} className={`nav-link ${currentPage === 'about' ? 'active' : ''}`}>Sobre nós</button></li>
                        <li><button onClick={() => handleNavigation('search')} className="nav-link">Buscar imóvel</button></li>
                        <li><button onClick={() => handleNavigation('favoritos')} className={`nav-link ${currentPage === 'favoritos' ? 'active' : ''}`}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: 'middle' }}>
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                          </svg>
                          Favoritos
                        </button></li>
                        <li><button onClick={() => handleNavigation('contato')} className={`nav-link ${currentPage === 'contato' ? 'active' : ''}`}>Contato</button></li>
                      </>
                    )}
                  </>
                )}
              </ul>
            </nav>

            <div className="header-contact">
              <a href="https://wa.me/5547988569815" className="phone-link" target="_blank" rel="noopener noreferrer">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="1.2em"
                  height="1.2em"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ marginRight: '8px', verticalAlign: 'middle' }}
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M20.52 3.48A11.82 11.82 0 0 0 12.06 0C5.5 0 .22 5.28.22 11.84c0 2.09.54 4.14 1.56 5.95L0 24l6.36-1.65c1.74.95 3.7 1.45 5.7 1.45h.01c6.56 0 11.84-5.28 11.84-11.84 0-3.17-1.24-6.14-3.39-8.48zM12.07 21.5h-.01c-1.75 0-3.46-.47-4.96-1.36l-.36-.21-3.77.98 1.01-3.67-.24-.38a9.77 9.77 0 0 1-1.52-5.11C2.22 6.4 6.6 2.02 12.06 2.02c2.62 0 5.08 1.02 6.93 2.88a9.74 9.74 0 0 1 2.86 6.94c0 5.46-4.38 9.66-9.78 9.66zm5.66-7.28c-.31-.16-1.82-.9-2.1-1-.28-.1-.48-.16-.68.16-.2.31-.78 1-.96 1.2-.18.2-.36.22-.67.06-.31-.16-1.3-.48-2.47-1.52-.91-.81-1.52-1.8-1.7-2.1-.18-.31-.02-.48.14-.64.14-.14.31-.36.46-.54.15-.18.2-.31.31-.52.1-.2.05-.38-.02-.54-.06-.16-.68-1.63-.93-2.23-.24-.58-.48-.5-.68-.5-.18 0-.38-.02-.58-.02-.2 0-.52.08-.79.38-.27.31-1.04 1.02-1.04 2.48 0 1.47 1.06 2.88 1.21 3.08.15.2 2.1 3.2 5.07 4.49.71.31 1.26.5 1.69.64.71.23 1.36.2 1.87.12.57-.08 1.82-.74 2.08-1.45.26-.71.26-1.32.18-1.45-.08-.12-.28-.2-.59-.36z" />
                </svg>
                (47) 98856-9815
              </a>
            </div>

            <button 
              className="menu-toggle"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </header>

      {/* Modal de autenticação de cliente */}
      <ClienteAuthModal
        isOpen={showClienteAuthModal}
        onClose={() => setShowClienteAuthModal(false)}
        onSuccess={() => {
          setShowClienteAuthModal(false);
          // O header será atualizado automaticamente via evento clienteUpdated
        }}
      />
    </>
  );
};

export default Header;